import { SalesTypeCodeEnum, StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
const IN_STOCK_PROBABILITY_SCORE = {
    [StoreAvailabilityProbabilityEnum.outOfStock]: 5,
    [StoreAvailabilityProbabilityEnum.lowInStock]: 0,
    [StoreAvailabilityProbabilityEnum.mediumInStock]: 0,
    [StoreAvailabilityProbabilityEnum.highInStock]: 0
};
const SALES_TYPE_SCORE = {
    [SalesTypeCodeEnum.contactStaff]: 2,
    [SalesTypeCodeEnum.department]: 1,
    [SalesTypeCodeEnum.aisleAndBin]: 0
};
/**
 * Everything sorting related for the ProductList.
 */
export default class ProductListSorting {
    /**
     * Sorts the product array in-place.
     *
     * @param shoppingProducts A list of shopping products.
     * @param sortBy The sorting order.
     * @param locale Locale string.
     * @param storeAvailabilities A list of availabilities for products in a certain store.
     * @returns A sorted array of shopping products.
     */
    static sort(shoppingProducts, sortBy, locale, storeAvailabilities) {
        const shoppingProductsCopy = shoppingProducts.slice();
        switch (sortBy) {
            case SortListOptionsEnum.packageWeight:
                return (this.sortByPackageProperty(shoppingProductsCopy, (measure) => measure.totalWeightGross));
            case SortListOptionsEnum.packageSize:
                return (this.sortByPackageProperty(shoppingProductsCopy, this.getMaxDimension));
            case SortListOptionsEnum.aisleLocation:
                return (this.sortByAisleLocation(shoppingProductsCopy, locale, storeAvailabilities));
            default:
                return shoppingProductsCopy;
        }
    }
    /**
     * Sorts by physical property (weight or height)
     * Puts biggest/heaviest products first.
     *
     * @param shoppingProducts ...
     * @param getPackageProperty A function which returns the physical property (e.g. Weight or size).
     */
    static sortByPackageProperty(shoppingProducts, getPackageProperty) {
        return shoppingProducts.sort((productA, productB) => {
            const packageMeasureA = ProductUtility.isBundleShoppingProduct(productA)
                ? null
                : productA.product.content?.packageMeasure;
            const packageMeasureB = ProductUtility.isBundleShoppingProduct(productB)
                ? null
                : productB.product.content?.packageMeasure;
            if (!packageMeasureA && !packageMeasureB) {
                return 0;
            }
            if (!packageMeasureA) {
                return -1;
            }
            if (!packageMeasureB) {
                return 1;
            }
            return getPackageProperty(packageMeasureB) - getPackageProperty(packageMeasureA);
        });
    }
    /**
     * Finds the maximum dimension from all included packages.
     *
     * @param packageMeasure Package measurements.
     * @returns The value of the maximum dimension.
     */
    static getMaxDimension(packageMeasure) {
        return packageMeasure.package.reduce((maxValue, productPackage) => {
            const { height, width, length } = productPackage;
            return Math.max(maxValue, Math.max(height, width, length));
        }, -1);
    }
    /**
     * Sorts by aisle and location, the order you would
     * find products when walking through an IKEA warehouse.
     * Products without storeAvailability are put last in the list.
     *
     * @param shoppingProducts ...
     * @param locale ...
     * @param storeAvailabilities ...
     */
    static sortByAisleLocation(shoppingProducts, locale, storeAvailabilities) {
        if (!storeAvailabilities) {
            return shoppingProducts;
        }
        const storeAvailabilityShoppingProducts = shoppingProducts.map((shoppingProduct) => ({
            shoppingProduct,
            availability: ProductUtility.isBundleShoppingProduct(shoppingProduct)
                ? undefined
                : this.getStoreAvailabilityForProduct(shoppingProduct, storeAvailabilities)
        }));
        return storeAvailabilityShoppingProducts
            .sort((storeAvailabilityProductA, storeAvailabilityProductB) => {
            const availabilityA = storeAvailabilityProductA.availability?.content?.cashCarry;
            const availabilityASalesLocation = availabilityA?.salesLocations?.[0];
            const availabilityB = storeAvailabilityProductB.availability?.content?.cashCarry;
            const availabilityBSalesLocation = availabilityB?.salesLocations?.[0];
            if (!availabilityASalesLocation?.type && !availabilityBSalesLocation?.type) {
                return 0;
            }
            if (!availabilityASalesLocation?.type) {
                return -1;
            }
            if (!availabilityBSalesLocation?.type) {
                return 1;
            }
            const scoreA = SALES_TYPE_SCORE[availabilityASalesLocation.type];
            const scoreB = SALES_TYPE_SCORE[availabilityBSalesLocation.type];
            if (scoreA === scoreB) {
                let locationScore;
                if (scoreA === 0) {
                    // Sales type "aisle and bin"
                    locationScore = this.compareByAisleAndBin(availabilityASalesLocation, availabilityBSalesLocation);
                }
                else if (scoreA === 1) {
                    // Sales type "department"
                    locationScore = this.compareByDepartment(availabilityASalesLocation, availabilityBSalesLocation, locale);
                }
                if (locationScore === 0) {
                    const availabilityAScore = availabilityA?.messageType
                        ? IN_STOCK_PROBABILITY_SCORE[availabilityA?.messageType]
                        : 0;
                    const availabilityBScore = availabilityB?.messageType
                        ? IN_STOCK_PROBABILITY_SCORE[availabilityB?.messageType]
                        : 0;
                    return availabilityBScore - availabilityAScore;
                }
                return locationScore;
            }
            // Put products with higher score before products with lower score
            return scoreB - scoreA;
        })
            .map(({ shoppingProduct }) => shoppingProduct);
    }
    static getStoreAvailabilityForProduct(shoppingProduct, storeAvailabilities) {
        const id = shoppingProduct.product.content?.ruItemNo;
        return storeAvailabilities.find((sa) => sa.content.itemNo === id);
    }
    /**
     * Custom sorting algorithm based on aisle & bin.
     * Assumes both products have SalesTypeCode AISLE_AND_BIN.
     *
     * @param availabilityALocation ...
     * @param availabilityBLocation ...
     */
    static compareByAisleAndBin(availabilityALocation, availabilityBLocation) {
        const aisleComparison = parseInt(availabilityALocation.aisle) - parseInt(availabilityBLocation.aisle);
        if (aisleComparison === 0) {
            return (parseInt(availabilityALocation.bin) - parseInt(availabilityBLocation.bin));
        }
        return aisleComparison;
    }
    /**
     * Compares two availabilities alphabetically by department name.
     * Assumes both products have SalesTypeCode DEPARTMENT.
     *
     * @param availabilityALocation ...
     * @param availabilityBLocation ...
     * @param locale ...
     */
    static compareByDepartment(availabilityALocation, availabilityBLocation, locale) {
        const departmentA = availabilityALocation.departmentName || '';
        const departmentB = availabilityBLocation.departmentName || '';
        // String.localeCompare can't handle null, so if locale isn't set we send in undefined instead.
        return departmentA.localeCompare(departmentB, locale || undefined);
    }
}
//# sourceMappingURL=ProductListSorting.js.map